import { useState, useEffect } from "react"
import { axios } from "@/contexts/auth"
import Layout from "@/components/layouts/DefaultLayout"
import LoadingSpinner from "@/components/LoadingSpinner"
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import { ReactComponent as CloudIcon } from "@/icons/cloud-icon.svg"

type BrandStats = {
  name: string
  count: number
  percentage: number
  color: string
}

type HeadphoneStats = {
  brand: string
  model: string
  presetCount: number
}

type PresetStats = {
  sharedPresets: number
}

const COLORS = [
  "#FFB800",
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEAA7",
  "#DDA0DD",
  "#98D8E8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E9",
  "#F8C471",
]

const HeadphoneStatesPage = () => {
  const [brandStats, setBrandStats] = useState<BrandStats[]>([])
  const [headphoneStats, setHeadphoneStats] = useState<HeadphoneStats[]>([])
  const [presetStats, setPresetStats] = useState<PresetStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true)
      try {
        // Fetch ALL public presets by paginating through all pages
        let allPresets = []
        let nextUrl = "/api/equalizer/public-presets/?page_size=100" // Start with first page
        let totalPresets = 0

        // Keep fetching pages until there are no more
        while (nextUrl) {
          const presetsResponse = await axios.get(nextUrl)
          const responseData = presetsResponse.data

          allPresets = [...allPresets, ...responseData.results]
          totalPresets = responseData.count // This should be the same for all pages
          nextUrl = responseData.next // Will be null when no more pages

          console.log(
            `Fetched ${responseData.results.length} presets, total so far: ${allPresets.length}`
          )
        }

        console.log(`Finished fetching all presets. Total: ${allPresets.length} of ${totalPresets}`)

        const presets = allPresets

        // Calculate brand statistics
        const brandCounts = {}
        const headphoneCounts = {}
        const userIds = new Set()

        presets.forEach((preset) => {
          // Count users
          userIds.add(preset.creator_id)

          // Count headphone models and brands
          if (preset.headphone) {
            const brandName = preset.headphone.brand.name
            const headphoneKey = `${brandName} ${preset.headphone.model}`

            brandCounts[brandName] = (brandCounts[brandName] || 0) + 1
            headphoneCounts[headphoneKey] = (headphoneCounts[headphoneKey] || 0) + 1
          }
        })

        // Create brand stats for pie chart
        const brandStatsData = Object.entries(brandCounts)
          .map(([name, count], index) => ({
            name,
            count: count as number,
            percentage: Math.round(
              ((count as number) / presets.filter((p) => p.headphone).length) * 100
            ),
            color: COLORS[index % COLORS.length],
          }))
          .sort((a, b) => b.count - a.count)

        // Create headphone stats for top list
        const headphoneStatsData = Object.entries(headphoneCounts)
          .map(([headphone, count]) => {
            const [brand, ...modelParts] = headphone.split(" ")
            return {
              brand,
              model: modelParts.join(" "),
              presetCount: count as number,
            }
          })
          .sort((a, b) => b.presetCount - a.presetCount)
          .slice(0, 20) // Top 20 headphones

        // Calculate preset statistics
        const sharedPresets = presets.length

        setBrandStats(brandStatsData)
        setHeadphoneStats(headphoneStatsData)
        setPresetStats({
          sharedPresets,
        })
      } catch (error) {
        console.error("Error fetching statistics:", error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [])

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-3 shadow-lg backdrop-blur-sm">
          <p className="font-medium text-white">{data.name}</p>
          <p className="text-sm text-silver">
            {data.count} presets ({data.percentage}%)
          </p>
        </div>
      )
    }
    return null
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex h-full items-center justify-center">
          <span className="flex items-center gap-2">
            <LoadingSpinner size="small" />
            <span className="text-xs text-gray-500">LOADING STATISTICS</span>
          </span>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="h-full overflow-y-auto px-14 py-6">
        <div className="mb-8">
          <h1 className="type-heading-xl mb-2 font-bold text-white">Core Headphone Usage Stats</h1>
          <p className="type-body-base text-silver">A current look at the Core community...</p>
        </div>

        {/* Preset Statistics Cards */}
        {presetStats && (
          <div className="mb-12 flex justify-center">
            <div className="w-full max-w-md rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-6">
              <div className="mb-2 flex items-center justify-center gap-3">
                <CloudIcon className="h-5 w-5 text-[#4ECDC4]" />
                <span className="type-body-sm font-medium text-silver">Shared Presets</span>
              </div>
              <div className="type-heading-lg text-center font-bold text-white">
                {presetStats.sharedPresets.toLocaleString()}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Headphone Brands Pie Chart */}
          <div className="rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-6">
            <h2 className="type-heading-md mb-6 font-bold text-white">Headphone Brands</h2>
            {brandStats.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={brandStats}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      label={({ name, percentage }) => `${name} (${percentage}%)`}
                      labelLine={false}
                    >
                      {brandStats.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={renderCustomTooltip} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-80 items-center justify-center">
                <p className="text-silver">No brand data available</p>
              </div>
            )}
          </div>

          {/* Most Popular Headphones List */}
          <div className="rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-6">
            <h2 className="type-heading-md mb-6 font-bold text-white">Most EQ'ed Headphones</h2>
            {headphoneStats.length > 0 ? (
              <div className="max-h-80 space-y-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-jaguar hover:[&::-webkit-scrollbar-thumb]:bg-silver [&::-webkit-scrollbar-track]:bg-dark-jaguar/40 [&::-webkit-scrollbar]:w-2">
                {headphoneStats.map((headphone, index) => (
                  <div
                    key={`${headphone.brand}-${headphone.model}`}
                    className="flex items-center gap-3 py-1"
                  >
                    <span className="type-body-xs w-6 font-bold text-[#FFB800]">#{index + 1}</span>
                    <span className="type-body-sm font-medium text-white">
                      {headphone.brand} {headphone.model}{" "}
                      <span className="type-body-xs text-silver">
                        ({headphone.presetCount} presets)
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-80 items-center justify-center">
                <p className="text-silver">No headphone data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Brand Distribution Bar Chart */}
        {brandStats.length > 0 && (
          <div className="mt-8 rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-6">
            <h2 className="type-heading-md mb-6 font-bold text-white">Brand Distribution</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={brandStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#383C4A" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#858484", fontSize: 12 }}
                    tickLine={{ stroke: "#383C4A" }}
                  />
                  <YAxis
                    tick={{ fill: "#858484", fontSize: 12 }}
                    tickLine={{ stroke: "#383C4A" }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar dataKey="count" fill="#FFB800" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default HeadphoneStatesPage
